import { format } from 'date-fns'
import {
  assetModelFormFields,
  GeckoAssetModel,
} from 'types/entries/geckoAssetModel'
import {
  FormField,
  getFormFieldByDataKey,
} from 'types/geckoFormTypesAndHelpers'
import {
  ExternalUserFieldAccess,
  GeckoAssetModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoVerticalTankFoundationJSON,
  InputType,
  LegacyConeAssetModelJSON,
  RolodexEntryJson,
  TankFlatRoofAssetModelJSON,
  TankFloorAssetModelJSON,
  UnitMagnitude,
  VerticalTankShellAssetModelJSON,
} from 'types/index'
import { assetModelPrecision } from 'utils/constants'
import { getDropdownOptionsForEnum } from 'utils/enum'
import { parseDate, parseNumber } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

import { GeckoComponentModelType } from './assetModelEnums'
import { InsulatedOptions } from './assetModelHelpers'
import { BaseAssetModelData } from './baseAssetModelData'
import isNil from 'lodash/isNil'

export type GeckoVerticalTankAssetModelJSON = {
  name: string
  type: GeckoAssetModelType.VerticalTank
  installation_date: string | undefined
  components: Record<
    string,
    | VerticalTankShellAssetModelJSON
    | TankFloorAssetModelJSON
    | TankFlatRoofAssetModelJSON
    | LegacyConeAssetModelJSON
    | GeckoVerticalTankFoundationJSON
  >
  effective_date: string
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
  insulated: string | undefined
  contents: string | undefined
  contents_ph: string | undefined
  surface_temperature: number | undefined
  fill_elevation: number | undefined
  specific_gravity: number | undefined
  calibrated_volume: number | undefined
}

//TODO: Add Cone and Remove Legacy Cone once rendering piece is done. [FLOW-540]
export const VerticalTankAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.VerticalTankShell,
  GeckoComponentModelType.TankFloor,
  GeckoComponentModelType.TankFlatRoof,
  GeckoComponentModelType.LegacyCone,
  GeckoComponentModelType.VerticalTankFoundation,
]

export const NewVerticalTankShellAssetModelJSON: GeckoVerticalTankAssetModelJSON =
  {
    name: 'New Asset Model Tank Shell',
    type: GeckoAssetModelType.VerticalTank,
    installation_date: undefined,
    components: {},
    effective_date: format(Date.now(), 'yyyy-MM-dd'),
    compass_orientation: undefined,
    plant_north_compass_orientation: undefined,
    insulated: undefined,
    contents: undefined,
    contents_ph: undefined,
    surface_temperature: undefined,
    fill_elevation: undefined,
    specific_gravity: undefined,
    calibrated_volume: undefined,
  }

export class GeckoVerticalTankAssetModel extends GeckoAssetModel<
  BaseAssetModelData & {
    insulated: InsulatedOptions | undefined
    contents: string | undefined
    contentsPH: string | undefined
    surfaceTemperature: GeckoMeasurement | undefined
    fillElevation: GeckoMeasurement | undefined
    specificGravity: number | undefined
    calibratedVolume: GeckoMeasurement | undefined
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(json)
    const recordData = json.data as Record<string, unknown>
    this.data.insulated = recordData.insulated
      ? (recordData.insulated as InsulatedOptions)
      : undefined
    this.data.contents = recordData.contents as string
    this.data.contentsPH = recordData.contents_ph as string

    const parsedSurfaceTemperature = parseNumber(recordData.surface_temperature)
    if (parsedSurfaceTemperature !== undefined) {
      this.data.surfaceTemperature = GeckoMeasurement.CreateFromValue(
        parsedSurfaceTemperature,
        GeckoMeasurementUnit.Celsius
      )
    }
    const parsedFillElevation = parseNumber(recordData.fill_elevation)

    if (parsedFillElevation !== undefined) {
      this.data.fillElevation = GeckoMeasurement.CreateFromValue(
        parsedFillElevation,
        GeckoMeasurementUnit.Meters
      )
    }
    this.data.specificGravity = parseNumber(recordData.specific_gravity)

    const parsedCalibratedVolume = parseNumber(recordData.calibrated_volume)
    if (!isNil(parsedCalibratedVolume)) {
      this.data.calibratedVolume = GeckoMeasurement.CreateFromValue(
        parsedCalibratedVolume,
        GeckoMeasurementUnit.Barrels
      )
    }
  }

  public static CreateFromJSON(
    json: RolodexEntryJson
  ): GeckoVerticalTankAssetModel {
    return new GeckoVerticalTankAssetModel(json)
  }

  public asJSON(): GeckoVerticalTankAssetModelJSON {
    return generateTankShellAssetModel(
      this.data.name,
      this.data.installation_date,
      this.raw.data.components as Record<
        string,
        | VerticalTankShellAssetModelJSON
        | TankFloorAssetModelJSON
        | TankFlatRoofAssetModelJSON
        | LegacyConeAssetModelJSON
      >,
      this.data.effective_date,
      this.data.compass_orientation,
      this.data.plant_north_compass_orientation,
      this.data.insulated as string,
      this.data.contents as string,
      this.data.contentsPH as string,
      this.data.surfaceTemperature as GeckoMeasurement,
      this.data.fillElevation as GeckoMeasurement,
      this.data.specificGravity as number,
      this.data.calibratedVolume
    )
  }
}

export function generateTankShellAssetModel(
  name: string,
  installation_date: Date | undefined,
  components: Record<
    string,
    | VerticalTankShellAssetModelJSON
    | TankFloorAssetModelJSON
    | TankFlatRoofAssetModelJSON
    | LegacyConeAssetModelJSON
    | GeckoVerticalTankFoundationJSON
  >,
  effective_date: Date,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined,
  insulated: string | undefined,
  contents: string | undefined,
  contentsPH: string | undefined,
  surfaceTemperature: GeckoMeasurement | undefined,
  fillElevation: GeckoMeasurement | undefined,
  specificGravity: number | undefined,
  calibratedVolume: GeckoMeasurement | undefined
): GeckoVerticalTankAssetModelJSON {
  const tankSurfaceTemperature =
    surfaceTemperature !== undefined
      ? surfaceTemperature
          .convertTo(GeckoMeasurementUnit.Celsius)
          .toPrecision(assetModelPrecision).num
      : undefined
  const tankFillElevation =
    fillElevation !== undefined
      ? fillElevation
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined
  const tankInstallationDate = installation_date
    ? installation_date.toISOString().split('T')[0]
    : undefined

  const tankEffectiveDate = effective_date.toISOString().split('T')[0]
  return {
    name: name,
    type: GeckoAssetModelType.VerticalTank,
    installation_date: tankInstallationDate,
    components: components,
    effective_date: tankEffectiveDate,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
    insulated: insulated,
    contents: contents,
    contents_ph: contentsPH,
    surface_temperature: tankSurfaceTemperature,
    fill_elevation: tankFillElevation,
    specific_gravity: specificGravity,
    calibrated_volume: calibratedVolume?.num,
  }
}

export const verticalTankShellModelFormFields = (
  assetModelJSON: GeckoVerticalTankAssetModelJSON
) => {
  const assetModelFields = assetModelFormFields(assetModelJSON)
  const tankShellModelFormFields = [
    {
      dataKey: 'insulated',
      label: 'Insulated',
      value: assetModelJSON.insulated,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: getDropdownOptionsForEnum(InsulatedOptions),
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    {
      dataKey: 'contents',
      label: 'Contents',
      value: assetModelJSON.contents,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'contents_ph',
      label: 'Contents PH',
      value: assetModelJSON.contents_ph,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'surface_temperature',
      label: 'Surface Temperature',
      value: assetModelJSON.surface_temperature,
      unit_magnitude: UnitMagnitude.FahrenheitOrCelsius,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'fill_elevation',
      label: 'Fill Elevation',
      value: assetModelJSON.fill_elevation,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'specific_gravity',
      label: 'Specific Gravity',
      value: assetModelJSON.specific_gravity,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'calibrated_volume',
      label: 'Calibrated Volume',
      value: assetModelJSON.calibrated_volume,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
  return [...assetModelFields, ...tankShellModelFormFields]
}

export const VerticalTankJSONFromFormFields = (
  assetModelJSON: GeckoVerticalTankAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedSurfaceTemperature = parseNumber(
    getFormFieldByDataKey(formFields, 'surface_temperature')?.value
  )
  const parsedFillElevation = parseNumber(
    getFormFieldByDataKey(formFields, 'fill_elevation')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const contents = getFormFieldByDataKey(formFields, 'contents')
  const insulated = getFormFieldByDataKey(formFields, 'insulated')
  const contentsPH = getFormFieldByDataKey(formFields, 'contents_ph')

  const calibratedVolumeFieldValue = parseNumber(
    getFormFieldByDataKey(formFields, 'calibrated_volume')?.value
  )
  const calibratedVolumeMeasurement = isNil(calibratedVolumeFieldValue)
    ? calibratedVolumeFieldValue
    : GeckoMeasurement.CreateFromValue(
        calibratedVolumeFieldValue,
        GeckoMeasurementUnit.Barrels
      )

  return generateTankShellAssetModel(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    assetModelJSON.components,
    parseDate(assetModelJSON.effective_date) as Date,
    parseNumber(
      getFormFieldByDataKey(formFields, 'compass_orientation')?.value
    ),
    parseNumber(
      getFormFieldByDataKey(formFields, 'plant_north_compass_orientation')
        ?.value
    ),
    insulated?.value !== InsulatedOptions.Unknown
      ? (insulated?.value as string)
      : undefined,
    contents?.value ? (contents?.value as string) : undefined,
    contentsPH?.value ? (contentsPH?.value as string) : undefined,
    parsedSurfaceTemperature !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedSurfaceTemperature,
          GeckoMeasurementUnit.Celsius
        )
      : undefined,
    parsedFillElevation !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedFillElevation,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parseNumber(getFormFieldByDataKey(formFields, 'specific_gravity')?.value),
    calibratedVolumeMeasurement
  )
}
